/**
 * Post-signup screen telling the user to check their email.
 * Receives the email via location.state from SignUpPage.
 */
import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { authClient } from '@/shared/auth/authClient';
import { Button } from '@/interfaces/learner/ui';
import AuthLayout from '@/shared/auth/AuthLayout';
import Logomark from '@/shared/components/Logomark';

interface LocationState {
  email?: string;
}

export default function EmailSentPage() {
  const location = useLocation();
  const email = (location.state as LocationState | null)?.email;
  const [resending, setResending] = useState(false);

  const onResend = async () => {
    if (!email) {
      toast.error('No email on file — please sign up again.');
      return;
    }
    setResending(true);
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: `${window.location.origin}/email-verified`,
    });
    setResending(false);
    if (error) {
      toast.error(error.message ?? 'Could not resend');
    } else {
      toast.success('Verification email resent');
    }
  };

  return (
    <AuthLayout>
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <Logomark size={48} />
        </div>
        <h1 className="text-2xl font-bold">Check your email</h1>
      </div>
      <div className="space-y-4 text-center">
        <p className="text-sm">
          We sent a verification link to <strong>{email ?? 'your email address'}</strong>. Click the
          link to activate your account.
        </p>
        <Button variant="secondary" size="md" onPress={onResend} isDisabled={resending}>
          {resending ? 'Sending...' : 'Resend verification email'}
        </Button>
        <div className="text-sm pt-2">
          <Link to="/login" className="text-[var(--color-accent-text)] hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}
